import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Link as LinkIcon, Upload, Paperclip } from "lucide-react";

/**
 * Inline resources manager for an admin lesson row.
 * Lessons are persisted first, then resources are attached via the backend.
 */
export default function LessonResourcesAdmin({ lesson, onChange }) {
  const [open, setOpen] = useState(false);
  const [resources, setResources] = useState(lesson.resources || []);
  const [adding, setAdding] = useState(null); // 'link' | 'pdf' | null
  const [linkTitle, setLinkTitle] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => { setResources(lesson.resources || []); }, [lesson]);

  const reload = async () => {
    try {
      const { data } = await api.get(`/lessons/${lesson.lesson_id}`);
      setResources(data.resources || []);
      onChange?.();
    } catch {}
  };

  const addLink = async (e) => {
    e.preventDefault();
    if (!linkTitle.trim() || !linkUrl.trim()) {
      toast.error("Title and URL required");
      return;
    }
    try {
      await api.post(`/admin/lessons/${lesson.lesson_id}/resources`, {
        type: "link", title: linkTitle.trim(), url: linkUrl.trim(),
      });
      setLinkTitle(""); setLinkUrl(""); setAdding(null);
      toast.success("Link added");
      reload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files allowed");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Max 50 MB");
      return;
    }
    const title = window.prompt("Title for this PDF:", file.name.replace(/\.pdf$/i, ""));
    if (!title) return;

    setUploading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload/pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
      });
      await api.post(`/admin/lessons/${lesson.lesson_id}/resources`, {
        type: "pdf", title, file_id: data.file_id, size: data.size, url: data.url,
      });
      toast.success(`Uploaded ${data.original_filename}`);
      setAdding(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      setUploadPct(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.title}"?`)) return;
    try {
      await api.delete(`/admin/lessons/${lesson.lesson_id}/resources/${r.resource_id}`);
      toast.success("Removed");
      reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`resources-btn-${lesson.lesson_id}`}
        className="text-white/40 hover:text-[#FF8A00] p-2 relative"
        title="Resources"
      >
        <Paperclip size={14} />
        {resources.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#FF8A00] text-[9px] font-semibold text-black flex items-center justify-center">
            {resources.length}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setAdding(null); } }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-lg" data-testid="resources-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Resources · <span className="text-white/60 text-base">{lesson.title}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="mt-3 space-y-2 max-h-[40vh] overflow-y-auto">
            {resources.length === 0 && (
              <p className="text-sm text-white/40 text-center py-6">No resources yet.</p>
            )}
            {resources.map((r) => (
              <div key={r.resource_id} data-testid={`resource-${r.resource_id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-[#0F0F0F] border border-white/5">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF8A00]/20 to-[#E52E71]/10 flex items-center justify-center shrink-0">
                  {r.type === "pdf" ? <FileText size={16} className="text-[#FF8A00]" /> : <LinkIcon size={16} className="text-[#FF8A00]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{r.title}</div>
                  <div className="text-[11px] text-white/40 truncate">
                    {r.type === "pdf" ? `PDF · ${fmtSize(r.size)}` : r.url}
                  </div>
                </div>
                <button data-testid={`remove-resource-${r.resource_id}`}
                  onClick={() => remove(r)}
                  className="text-white/40 hover:text-[#E52E71] p-2">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 border-t border-white/5 pt-4">
            {!adding && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setAdding("link")} data-testid="add-resource-link"
                  className="rounded-full bg-white/10 hover:bg-white/15 border border-white/10">
                  <LinkIcon size={14} className="mr-2" /> Add link
                </Button>
                <Button onClick={() => { setAdding("pdf"); fileRef.current?.click(); }}
                  data-testid="add-resource-pdf"
                  disabled={uploading}
                  className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                  <Upload size={14} className="mr-2" />
                  {uploading ? `Uploading ${uploadPct}%` : "Upload PDF"}
                </Button>
                <input ref={fileRef} type="file" accept="application/pdf,.pdf"
                  className="hidden" onChange={(e) => uploadPdf(e.target.files?.[0])} />
              </div>
            )}
            {adding === "link" && (
              <form onSubmit={addLink} className="space-y-3">
                <div>
                  <Label className="text-xs text-white/60">Title</Label>
                  <Input value={linkTitle} onChange={(e) => setLinkTitle(e.target.value)}
                    required data-testid="resource-link-title"
                    placeholder="e.g. NIH study on BPC-157"
                    className="bg-[#0F0F0F] border-white/10 mt-1"/>
                </div>
                <div>
                  <Label className="text-xs text-white/60">URL</Label>
                  <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)}
                    required data-testid="resource-link-url"
                    placeholder="https://…"
                    className="bg-[#0F0F0F] border-white/10 mt-1"/>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline"
                    onClick={() => setAdding(null)}
                    className="rounded-full bg-transparent border-white/10">Cancel</Button>
                  <Button type="submit" data-testid="resource-link-submit"
                    className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">Add</Button>
                </div>
              </form>
            )}
            {uploading && (
              <div className="mt-3 h-1 rounded-full bg-[#0F0F0F] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]"
                     style={{ width: `${uploadPct}%` }} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function fmtSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb/1024).toFixed(1)} MB`;
}
